import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cachedBranding, fetchBranding, brandingLogoUrl } from "@/lib/api";
import { LogOut, LayoutDashboard, PackageOpen, Boxes, Truck, FileText, UserCheck, ShoppingCart, Users, Store, Camera, BarChart3, TrendingUp, Settings, Menu, Trophy, Wallet, QrCode, ShieldCheck, Warehouse as WarehouseIcon, ContactRound } from "lucide-react";

const navTestId = (to) => {
  const [path, qs] = to.split("?");
  const base = path.replace(/\//g, "") || "home";
  return `nav-${base}${qs ? "-" + qs.replace(/[^a-z0-9]/gi, "") : ""}`;
};

const roleNav = {
  admin: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/gudang", label: "Gudang", icon: WarehouseIcon },
    { to: "/toko-admin", label: "Toko", icon: Store },
    { to: "/toko-admin?tab=customers", label: "Pembeli", icon: ContactRound },
    { to: "/toko-admin?tab=affiliates", label: "Affiliate", icon: Users },
    { to: "/aktivitas", label: "Aktivitas", icon: Camera },
    { to: "/inventaris", label: "Stok", icon: Boxes },
    { to: "/absensi", label: "Absensi", icon: UserCheck },
    { to: "/verifikasi-absen", label: "Verifikasi", icon: ShieldCheck },
    { to: "/permintaan", label: "Permintaan", icon: ShoppingCart },
    { to: "/pengeluaran", label: "Biaya", icon: Wallet },
    { to: "/kinerja", label: "Kinerja", icon: Trophy },
    { to: "/pengguna", label: "Karyawan", icon: Users },
    { to: "/cabang", label: "Cabang", icon: Store },
    { to: "/produk", label: "Produk", icon: PackageOpen },
    { to: "/pengaturan", label: "Pengaturan", icon: Settings },
    { to: "/bagikan", label: "Bagikan", icon: QrCode },
  ],
  gudang: [
    { to: "/gudang", label: "Gudang", icon: PackageOpen },
    { to: "/toko-admin", label: "Toko", icon: Store },
    { to: "/inventaris", label: "Stok", icon: Boxes },
    { to: "/absensi", label: "Absensi", icon: UserCheck },
    { to: "/permintaan", label: "Permintaan", icon: ShoppingCart },
  ],
  cabang: [
    { to: "/cabang-kota", label: "Cabang", icon: Truck },
    { to: "/toko-admin", label: "Toko", icon: Store },
    { to: "/resi", label: "Resi", icon: FileText },
    { to: "/absensi", label: "Absensi", icon: UserCheck },
    { to: "/permintaan", label: "Permintaan", icon: ShoppingCart },
  ],
  keuangan: [
    { to: "/resi", label: "Resi", icon: FileText },
    { to: "/laporan", label: "Laporan", icon: BarChart3 },
    { to: "/omset", label: "Omset", icon: TrendingUp },
    { to: "/pengeluaran", label: "Biaya", icon: Wallet },
    { to: "/kinerja", label: "Kinerja", icon: Trophy },
    { to: "/absensi", label: "Absensi", icon: UserCheck },
    { to: "/permintaan", label: "Permintaan", icon: ShoppingCart },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const items = roleNav[user?.role] || [];
  const [brand, setBrand] = useState(cachedBranding() || { app_name: "GudangFlow", has_logo: false });
  useEffect(() => {
    fetchBranding().then((b) => { setBrand(b); document.title = b.app_name; });
  }, []);
  const initials = brand.app_name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "GF";

  const handleLogout = () => { logout(); nav("/login"); };

  const roleBadge = {
    admin: { text: "Bos / Admin", cls: "bg-blue-100 text-blue-800" },
    gudang: { text: "Gudang Pusat", cls: "bg-amber-100 text-amber-800" },
    cabang: { text: "Cabang Kota", cls: "bg-emerald-100 text-emerald-800" },
    keuangan: { text: "Keuangan", cls: "bg-violet-100 text-violet-800" },
  }[user?.role] || { text: "-", cls: "" };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky mobile header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {brand.has_logo ? (
              <img data-testid="brand-logo" src={brandingLogoUrl()} alt={brand.app_name} className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-extrabold" style={{fontFamily:'Manrope'}}>{initials}</div>
            )}
            <div className="min-w-0">
              <p data-testid="brand-name" className="text-sm font-semibold text-slate-900 truncate" style={{fontFamily:'Manrope'}}>{brand.app_name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="role-badge" className={`hidden sm:inline-block px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${roleBadge.cls}`}>{roleBadge.text}</span>
            <button data-testid="logout-btn" onClick={handleLogout} className="w-10 h-10 rounded-lg border border-slate-200 hover:bg-slate-100 flex items-center justify-center text-slate-700 active:scale-95 transition-transform">
              <LogOut size={18} />
            </button>
          </div>
        </div>
        {/* Desktop tab nav */}
        <div className="hidden md:block border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto no-scrollbar">
            {items.map((it) => {
              const Icon = it.icon;
              const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
              return (
                <NavLink key={it.to} to={it.to} data-testid={navTestId(it.to)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${active ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
                  <Icon size={16} /> {it.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 md:py-8 pb-24 md:pb-8">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-slate-200 flex justify-around items-center px-2 z-50">
        {items.slice(0, 4).map((it) => {
          const Icon = it.icon;
          const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
          return (
            <NavLink key={it.to} to={it.to} data-testid={`mobnav-${it.to.replace(/\//g,'') || 'home'}`}
              className={`flex-1 h-full flex flex-col items-center justify-center gap-1 ${active ? 'text-blue-700' : 'text-slate-500'}`}>
              <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
              <span className="text-[10px] font-semibold uppercase tracking-wider">{it.label}</span>
            </NavLink>
          );
        })}
        <button data-testid="mobnav-more" onClick={() => setMoreOpen(true)} className="flex-1 h-full flex flex-col items-center justify-center gap-1 text-slate-500">
          <Menu size={20} />
          <span className="text-[10px] font-semibold uppercase tracking-wider">Lainnya</span>
        </button>
      </nav>

      {/* Mobile sheet "Lainnya" — semua menu admin/role */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-slate-900/50" onClick={() => setMoreOpen(false)}>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 pb-8 space-y-1" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Semua Menu</p>
            {items.map((it) => {
              const Icon = it.icon;
              const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
              return (
                <NavLink key={it.to} to={it.to} onClick={() => setMoreOpen(false)} data-testid={`sheet-${it.to.replace(/\//g,'') || 'home'}`}
                  className={`flex items-center gap-3 px-4 h-12 rounded-xl font-semibold ${active ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}>
                  <Icon size={18} /> {it.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
